import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserPrefs } from '../context/UserPrefs';
import Header from '../Header/Header';
import LoginForm from '../Form/LoginForm';
import TwoFactorForm from '../Form/TwoFactorForm';
import TotpEnrollForm from '../Form/TotpEnrollForm';
import Footer from '../Footer/Footer';
import BrandCopyright from '../Common/BrandCopyright';
import LoginCard from '../Layout/LoginCard';
import Background from '../Background/Background';
import senerfiLogo from '../../assets/images/Senerfi.gif';
import senerfiBg from '../../assets/images/senerfi-bg.png';
import senerfitoWsp from '../../assets/images/senerfito-wsp.png';
import soporteSenerfitoGif from '../../assets/images/soportesenerfito.gif';
import { HelpDeskModal } from '../HelpDesk/HelpDeskModal';
import { api } from '../../services/api';
import { createTicketSnapshotDataUrl } from '../HelpDesk/ticketSnapshot';
import { isNativeRuntime } from '../../utils/runtimePlatform';
import { Download, Smartphone } from 'lucide-react';

function reportSigLoginDebug() {}

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [logoutMsg, setLogoutMsg] = useState(false);
  const navigate = useNavigate();
  const { currentUser, authenticate, verify2FA, resend2FA, startTotpEnrollment, completeTotpEnrollment } = useUserPrefs();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const passwordRef = useRef(null);
  const isNative = isNativeRuntime();
  const isSigMobile = String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === 'sig_mobile';

  // Estados para 2FA
  const [require2FA, setRequire2FA] = useState(false);
  const [tempUserId, setTempUserId] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorRecipient, setTwoFactorRecipient] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState('email');
  const [isVerifying, setIsVerifying] = useState(false);

  // Estados para activación obligatoria de TOTP (primer login sin 2FA activo)
  const [requireTotpSetup, setRequireTotpSetup] = useState(false);
  const [enrollmentToken, setEnrollmentToken] = useState(null);
  const [enrollAccountEmail, setEnrollAccountEmail] = useState('');
  const [enrollLoadingSecret, setEnrollLoadingSecret] = useState(false);
  const [enrollSecretGrouped, setEnrollSecretGrouped] = useState('');
  const [enrollOtpauthUri, setEnrollOtpauthUri] = useState('');
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollSubmitting, setEnrollSubmitting] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [helpDeskOpen, setHelpDeskOpen] = useState(false);
  const [helpDeskSubmitting, setHelpDeskSubmitting] = useState(false);
  const [helpDeskForm, setHelpDeskForm] = useState({ name: '', email: '', dni: '', subject: '', category: '', department: '', description: '', priority: 'Media' });

  // El registro de horas extra sin sesión (botón naranja del login) se retiró
  // en oct-2026: ahora se registra solo dentro de NOVA, con concepto y
  // cálculo del valor (ver NovedadesHorasExtras en ContabilidadPanel.jsx).

  useEffect(() => {
    try {
      const v = sessionStorage.getItem('senerfi_show_logout');
      if (v) {
        setLogoutMsg(true);
        sessionStorage.removeItem('senerfi_show_logout');
      }
    } catch (_) {}
  }, []);
  useEffect(() => {
    const handler = () => setHelpDeskOpen(true);
    try { window.addEventListener('senerfi_helpdesk_open', handler); } catch (_) {}
    return () => { try { window.removeEventListener('senerfi_helpdesk_open', handler); } catch (_) {} };
  }, []);
  useEffect(() => {
    if (!logoutMsg) return;
    const t = setTimeout(() => setLogoutMsg(false), 3000);
    return () => clearTimeout(t);
  }, [logoutMsg]);

  const handleLoginSuccess = useCallback((user) => {
    const isNative = isNativeRuntime();
    const nativeApp = String(process.env.REACT_APP_NATIVE_APP || '').trim().toLowerCase();
    const sede = user?.warehouseId || 'popayan';
    // #region debug-point D:login-success
    reportSigLoginDebug('D', 'Login success antes de navegar', {
      isNative,
      nativeApp,
      sede,
      role: user?.role || '',
      hasUserId: !!user?.id,
    });
    // #endregion
    if (isNative && nativeApp === 'sig') {
      navigate(`/mobile/sig/${sede}`);
    } else {
      // Variante completa del APK (sin REACT_APP_NATIVE_APP o distinta de
      // "sig"): mismo panel de Sede con todos los módulos que en la web,
      // en vez de quedar limitado solo a Control Operativo.
      navigate(`/sede/${sede}`);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isSigMobile || require2FA || requireTotpSetup || !currentUser) return;
    handleLoginSuccess(currentUser);
  }, [currentUser, handleLoginSuccess, isSigMobile, require2FA, requireTotpSetup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // #region debug-point A:login-submit
    reportSigLoginDebug('A', 'Inicio submit login', {
      isNative,
      hasEmail: !!String(email || '').trim(),
      emailLike: String(email || '').includes('@'),
      appVariant: String(process.env.REACT_APP_APP_VARIANT || '').trim(),
    });
    // #endregion
    const result = await authenticate(email, password);
    // #region debug-point B:login-result
    reportSigLoginDebug('B', 'Resultado authenticate', {
      success: !!result?.success,
      require2fa: !!result?.require_2fa,
      hasUser: !!result?.user,
      userId: result?.userId || result?.user?.id || null,
      message: result?.message || '',
    });
    // #endregion
    if (result.success) {
      if (result.require_totp_setup) {
        setTempUserId(result.userId);
        setEnrollmentToken(result.enrollmentToken);
        setEnrollAccountEmail(String(email || '').trim());
        setRequireTotpSetup(true);
        setEnrollLoadingSecret(true);
        const startRes = await startTotpEnrollment(result.userId, result.enrollmentToken);
        setEnrollLoadingSecret(false);
        if (startRes?.ok) {
          setEnrollSecretGrouped(startRes.secret_grouped || startRes.secret || '');
          setEnrollOtpauthUri(startRes.otpauth_uri || '');
        } else {
          setEnrollError(startRes?.error || 'No fue posible generar la clave de activación.');
        }
      } else if (result.require_2fa) {
        setTempUserId(result.userId);
        setTwoFactorRecipient(String(result.recipient || '').trim());
        setTwoFactorMethod(result.method === 'totp' ? 'totp' : 'email');
        setRequire2FA(true);
      } else {
        if (result.pendingTotpApproval) {
          alert('Tu verificación en dos pasos por app quedó registrada, pero está pendiente de que un Administrador la apruebe. Mientras tanto puedes seguir usando el sistema normalmente.');
        }
        handleLoginSuccess(result.user);
      }
    } else {
      alert(result.message || 'Credenciales inválidas (Comprueba tus datos).');
      if (passwordRef.current) {
        passwordRef.current.focus();
      }
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFactorCode) return;
    setIsVerifying(true);
    // #region debug-point C:verify-2fa-start
    reportSigLoginDebug('C', 'Inicio verificacion 2FA', {
      userId: tempUserId,
      codeLength: String(twoFactorCode || '').length,
    });
    // #endregion
    const result = await verify2FA(tempUserId, twoFactorCode);
    setIsVerifying(false);
    // #region debug-point E:verify-2fa-result
    reportSigLoginDebug('E', 'Resultado verificacion 2FA', {
      success: !!result?.success,
      hasUser: !!result?.user,
      message: result?.message || '',
    });
    // #endregion

    if (result.success) {
      handleLoginSuccess(result.user);
    } else {
      alert(result.message || 'Código inválido');
    }
  };

  const handleConfirmTotpEnroll = async (e) => {
    e.preventDefault();
    if (enrollCode.length !== 6) return;
    setEnrollSubmitting(true);
    setEnrollError('');
    const result = await completeTotpEnrollment(tempUserId, enrollmentToken, enrollCode);
    setEnrollSubmitting(false);
    if (result.success && result.pendingApprovalBlocked) {
      alert(result.message || 'Código confirmado. Debes esperar a que un Administrador apruebe tu activación antes de poder ingresar.');
      handleCancelTotpEnroll();
      return;
    }
    if (result.success) {
      handleLoginSuccess(result.user);
    } else {
      setEnrollError(result.message || 'Código inválido');
    }
  };

  const handleCancelTotpEnroll = () => {
    setRequireTotpSetup(false);
    setTempUserId(null);
    setEnrollmentToken(null);
    setEnrollAccountEmail('');
    setEnrollSecretGrouped('');
    setEnrollOtpauthUri('');
    setEnrollCode('');
    setEnrollError('');
  };

  const formatTicketCode = (id) => {
    const n = Number(id);
    if (!Number.isFinite(n) || n <= 0) return 'TK-000';
    return `TK-${String(n).padStart(3, '0')}`;
  };

  const submitHelpDesk = async () => {
    const payload = {
      name: String(helpDeskForm.name || '').trim(),
      email: String(helpDeskForm.email || '').trim(),
      dni: String(helpDeskForm.dni || '').trim(),
      subject: String(helpDeskForm.subject || '').trim(),
      category: String(helpDeskForm.category || '').trim(),
      department: String(helpDeskForm.department || '').trim(),
      description: String(helpDeskForm.description || '').trim(),
      priority: String(helpDeskForm.priority || 'Media').trim(),
    };
    if (!payload.name || !payload.email || !payload.dni || !payload.subject || !payload.category || !payload.description) {
      alert('Por favor complete los campos obligatorios: Nombre, Correo, Cédula, Título, Categoría y Descripción.');
      return;
    }
    setHelpDeskSubmitting(true);
    try {
      const createdAtLabel = new Date().toLocaleString('es-CO');
      const desc = [
        payload.department ? `Área/Departamento: ${payload.department}` : null,
        payload.description,
      ].filter(Boolean).join('\n\n');
      const res = await api.admin.supports.upsert({
        subject: payload.subject,
        description: desc,
        category: payload.category,
        department: payload.department || null,
        priority: payload.priority,
        requester_name: payload.name,
        requester_dni: payload.dni,
        requester_email: payload.email,
        status: 'Abierto',
      });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible crear el ticket');
      const ticketId = res?.id;
      const ticketCode = formatTicketCode(ticketId);
      const publicToken = String(res?.public_token || '').trim();

      try {
        localStorage.setItem('senerfi_ui_admin_ti_view', 'soportes');
        localStorage.setItem('senerfi_ui_admin_ti_group', 'gestion');
      } catch (_) {}

      try {
        const dataUrl = createTicketSnapshotDataUrl({
          ticketCode,
          subject: payload.subject,
          status: 'Abierto',
          priority: payload.priority,
          category: payload.category,
          requesterName: payload.name,
          requesterDni: payload.dni,
          requesterEmail: payload.email,
          createdAt: createdAtLabel,
          department: payload.department,
        });
        if (dataUrl) {
          await api.admin.supports.snapshot({ ticket_id: ticketId, image_data: dataUrl, public_token: publicToken || undefined });
        }
      } catch (_) {}

      let emailSent = true;
      try {
        await api.admin.supports.notify({ ticket_id: ticketId, public_token: publicToken || undefined });
      } catch (e) {
        emailSent = false;
      }

      setHelpDeskOpen(false);
      alert(
        emailSent
          ? `Ticket creado: ${ticketCode}\nSe envió una copia al correo: ${payload.email}`
          : `Ticket creado: ${ticketCode}\nNo fue posible enviar la copia al correo en este momento.`
      );
      setHelpDeskForm({ name: payload.name, email: payload.email, dni: payload.dni, subject: '', category: '', department: '', description: '', priority: 'Media' });
    } catch (e) {
      alert(e?.message || 'Error enviando el ticket.');
    } finally {
      setHelpDeskSubmitting(false);
    }
  };

  const handleResend2FA = async () => {
    if (!tempUserId) return;
    const result = await resend2FA(tempUserId);
    if (result.recipient) setTwoFactorRecipient(String(result.recipient || '').trim());
    alert(result.message);
  };

  const handleCancel2FA = () => {
    setRequire2FA(false);
    setTempUserId(null);
    setTwoFactorCode('');
    setTwoFactorRecipient('');
    setTwoFactorMethod('email');
  };

  // Eliminado: lógica de confirmación de sede

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-8 sm:py-10">
      <style>{`
        @keyframes senerfiFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes senerfiRingPulse {
          0% { opacity: 0.7; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.35); }
        }
      `}</style>

      <Background
        imageUrl={senerfiBg}
        gradient={false}
      />

      {!isNative && !isSigMobile && (
        <a
          href="https://senerfisas.com/uploads/descargas/NOVA-Senerfi.apk"
          className="fixed top-4 left-4 z-50 flex items-center gap-3 rounded-2xl border border-white/20 bg-white/95 shadow-lg backdrop-blur-sm px-3 py-2.5 hover:bg-white transition-colors"
          style={{ maxWidth: '15.5rem' }}
        >
          <span
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #16A34A 0%, #22C55E 100%)' }}
          >
            <Smartphone className="h-5 w-5 text-white" />
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold text-gray-800 leading-tight">Descarga la App</span>
            <span className="flex items-center gap-1 text-[11px] text-gray-500 leading-tight">
              <Download className="h-3 w-3 shrink-0" /> Android · NOVA-Senerfi.apk
            </span>
          </span>
        </a>
      )}


      {/* Login Card */}
      <LoginCard
        header={
          <Header
            heightClass="h-[18vh] sm:h-[16vh]"
            whiteWidthClass="w-2/3 sm:w-1/2"
            whiteHeightClass="h-[45%]"
            borderClass=""
          >
            <img 
              src={senerfiLogo}
              alt="Senerfi S.A.S." 
              className="w-[80%] sm:w-[70%] md:w-[70%] lg:w-[65%]"
            />
          </Header>
        }
      >
        {logoutMsg && (
          <div className="mb-3 px-3 py-2 rounded-lg border text-xs text-center" style={{ backgroundColor: '#EAFBF2', borderColor: '#CDEEE0', color: '#059669' }}>
            Sesión cerrada correctamente
          </div>
        )}
        {isNative && !isSigMobile && (
          <div className="mb-3 px-3 py-2 rounded-lg border text-xs text-center" style={{ backgroundColor: '#E0F2FE', borderColor: '#93C5FD', color: '#0A3A8B' }}>
            Accederás al modo móvil según tu sede.
          </div>
        )}
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 text-center">
          {requireTotpSetup ? 'Activación obligatoria' : require2FA ? 'Verificación de Seguridad' : 'Iniciar Sesión'}
        </h2>
        {requireTotpSetup ? (
          <TotpEnrollForm
            accountEmail={enrollAccountEmail}
            loadingSecret={enrollLoadingSecret}
            secretGrouped={enrollSecretGrouped}
            otpauthUri={enrollOtpauthUri}
            code={enrollCode}
            setCode={setEnrollCode}
            handleConfirm={handleConfirmTotpEnroll}
            handleCancel={handleCancelTotpEnroll}
            loading={enrollSubmitting}
            error={enrollError}
          />
        ) : require2FA ? (
          <TwoFactorForm
            code={twoFactorCode}
            setCode={setTwoFactorCode}
            handleVerify={handleVerify2FA}
            handleCancel={handleCancel2FA}
            handleResend={handleResend2FA}
            loading={isVerifying}
            recipient={twoFactorRecipient}
            method={twoFactorMethod}
          />
        ) : (
          <LoginForm
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
            handleSubmit={handleSubmit}
            passwordRef={passwordRef}
          />
        )}

        {/* Footer */}
        <Footer />
      </LoginCard>

      {!isNative && (
        <BrandCopyright colorClass="text-white" />
      )}

      {/* Eliminado: Modal de selección de sede */}

      {/* Botón flotante de chat en línea (esquina inferior izquierda).
          Antes iba abajo a la derecha y en z-50, igual que los modales, así
          que quedaba encima de ellos tapando el botón "Guardar" (alineado a
          la derecha). En z-40 los formularios abiertos lo cubren. */}
      <button
        type="button"
        aria-label="Abrir Mesa de Ayuda"
        className={`fixed ${isNative ? 'bottom-0 left-0' : 'bottom-4 left-4'} z-40 p-0 bg-transparent`}
        onClick={() => {
          setHelpDeskOpen(true);
        }}
      >
        <img
          src={senerfitoWsp}
          alt="Chat en línea Senerfito"
          className={isNative ? 'h-[0.9cm] w-[0.9cm] p-[0.5mm]' : 'h-[1.2cm] w-[1.2cm] p-[0.5mm]'}
        />
      </button>

      <HelpDeskModal
        isOpen={helpDeskOpen}
        onClose={() => setHelpDeskOpen(false)}
        avatarSrc={soporteSenerfitoGif}
        values={helpDeskForm}
        submitting={helpDeskSubmitting}
        onChange={(patch) => setHelpDeskForm((p) => ({ ...p, ...patch }))}
        onReset={() => setHelpDeskForm({ name: '', email: '', dni: '', subject: '', category: '', department: '', description: '', priority: 'Media' })}
        onSubmit={submitHelpDesk}
      />


    </div>
  );
}
